import React, { useRef } from "react";
import { Carousel } from "antd";
import { bannerMaterialSrc, bannerSwiperImages } from "./bannerData";
import { LocalizedLink } from "../i18n/LocaleRouting";

const BANNER_AUTOPLAY_MS = 6200;
const SLIDE_DRAG_THRESHOLD_PX = 12;

export default function Banner() {
  const slideGestureRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    hasDragged: boolean;
  } | null>(null);
  const suppressSlideClickRef = useRef(false);

  const handleSlidePointerDown = (e: React.PointerEvent<HTMLElement>) => {
    suppressSlideClickRef.current = false;
    slideGestureRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      hasDragged: false,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handleSlidePointerMove = (e: React.PointerEvent<HTMLElement>) => {
    const g = slideGestureRef.current;
    if (!g || e.pointerId !== g.pointerId) return;
    const dx = Math.abs(e.clientX - g.startX);
    const dy = Math.abs(e.clientY - g.startY);
    if (dx > SLIDE_DRAG_THRESHOLD_PX || dy > SLIDE_DRAG_THRESHOLD_PX) {
      g.hasDragged = true;
    }
  };

  const handleSlidePointerUp = (e: React.PointerEvent<HTMLElement>) => {
    const g = slideGestureRef.current;
    if (g && e.pointerId === g.pointerId) {
      suppressSlideClickRef.current = g.hasDragged;
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* release may throw if capture already lost */
      }
    }
    slideGestureRef.current = null;
  };

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (suppressSlideClickRef.current) {
      e.preventDefault();
      e.stopPropagation();
      suppressSlideClickRef.current = false;
    }
  };

  return (
    <section className="w-full relative overflow-x-hidden bg-[#000]">
      {/* Fluid height (~2.4:1): scales with width, clamped for tiny/large viewports */}
      <article className="relative w-full overflow-hidden xl:h-[clamp(240px,44vw,840px)] lg:h-[clamp(240px,48vw,840px)] md:h-[clamp(240px,50vw,840px)] sm:h-[clamp(240px,56vw,940px)] h-[clamp(240px,50vw,840px)]">
        <Carousel
          autoplay
          autoplaySpeed={BANNER_AUTOPLAY_MS}
          speed={900}
          draggable
          swipe
          className="banner-hero-carousel"
        >
          {bannerSwiperImages.map(({ name, title, productPath }, index) => (
            <LocalizedLink
              key={title}
              to={productPath}
              className="banner-hero-slide"
              onPointerDown={handleSlidePointerDown}
              onPointerMove={handleSlidePointerMove}
              onPointerUp={handleSlidePointerUp}
              onPointerCancel={handleSlidePointerUp}
              onClick={handleLinkClick}
            >
              <img
                src={`${bannerMaterialSrc}/${name}`}
                alt={title}
                loading={index === 0 ? "eager" : "lazy"}
                ref={(el) => {
                  if (el) {
                    el.setAttribute(
                      "fetchpriority",
                      index === 0 ? "high" : "auto",
                    );
                  }
                }}
                className="banner-hero-image xl:max-w-[88%] lg:max-w-[94%] md:max-w-[100%] sm:w-full mx-auto"
                draggable={false}
                onDragStart={(e) => e.preventDefault()}
              />
            </LocalizedLink>
          ))}
        </Carousel>
      </article>
    </section>
  );
}
